import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogOut, LayoutDashboard, Settings } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import NotificationBell from "@/components/jamm/NotificationBell";

const navLinks = [
  { to: "/", label: "Accueil" },
  { to: "/accompagnateurs", label: "Accompagnateurs", showIfVisible: true },
  { to: "/tarifs", label: "Tarifs", authOnly: true, showIfTarifsVisible: true },
  { to: "/confidentialite", label: "Confidentialité" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isSuper = user?.role === "superadmin";
  const isAdmin = user?.role === "admin" || isSuper;
  const visibleLinks = navLinks.filter((l) => {
    if (l.showIfVisible && !settings.accompagnateurs_visible) return false;
    if (l.showIfTarifsVisible && !settings.tarifs_visible && !isAdmin) return false;
    if (l.authOnly && !user) return false;
    return true;
  });

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 glass border-b border-border/60"
    >
      <div className="container-jamm flex items-center justify-between h-24 md:h-32">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          {settings.logo_url ? (
            <img
              src={settings.logo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}` : settings.logo_url}
              alt={settings.app_name || "Sama Écoute"}
              className="h-20 md:h-28 w-auto object-contain drop-shadow-sm"
              data-testid="header-logo-img"
            />
          ) : (
            <>
              <span className="grid place-items-center w-9 h-9 rounded-full bg-sage-500 text-white font-display text-xl shadow-md group-hover:bg-sage-600 transition">{(settings.app_name || "S")[0]}</span>
              <span className="font-display text-2xl text-ink leading-none">{(settings.app_name || "Sama Écoute").split(" ")[0]} <span className="text-sage-600">{(settings.app_name || "Sama Écoute").split(" ").slice(1).join(" ")}</span></span>
            </>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {visibleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive ? "text-sage-700 bg-sage-50" : "text-ink-soft hover:text-sage-600 hover:bg-sage-50/60"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <NotificationBell />
              {isSuper && (
                <Link to="/admin/parametres" className="btn-outline !py-2 !px-5 text-sm" data-testid="header-settings-btn">
                  <Settings className="w-4 h-4" /> Paramètres
                </Link>
              )}
              <Link to={user.role === "admin" || user.role === "superadmin" ? "/admin" : "/dashboard"} className="btn-outline !py-2 !px-5 text-sm" data-testid="header-dashboard-btn">
                <LayoutDashboard className="w-4 h-4" /> Espace
              </Link>
              <button onClick={handleLogout} className="btn-pill bg-ink text-white px-5 py-2 text-sm hover:bg-ink-soft transition" data-testid="header-logout-btn">
                <LogOut className="w-4 h-4" /> Déconnexion
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-outline !py-2 !px-5 text-sm" data-testid="header-login-btn">
                Se connecter
              </Link>
              <Link to="/register" className="btn-primary !py-2 !px-5 text-sm" data-testid="header-register-btn">
                Créer un compte
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-full hover:bg-sage-50"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          data-testid="header-mobile-toggle"
        >
          {open ? <X className="w-6 h-6 text-ink" /> : <Menu className="w-6 h-6 text-ink" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border/60 bg-white/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="container-jamm py-4 flex flex-col gap-1">
            {visibleLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base font-medium ${isActive ? "text-sage-700 bg-sage-50" : "text-ink-soft hover:bg-sage-50/60"}`
                }
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="h-px bg-border my-2" />
            {user ? (
              <>
                {isSuper && (
                  <Link to="/admin/parametres" onClick={() => setOpen(false)} className="btn-outline justify-center mb-2" data-testid="mobile-settings-btn">
                    Paramètres du site
                  </Link>
                )}
                <Link to={user.role === "admin" || user.role === "superadmin" ? "/admin" : "/dashboard"} onClick={() => setOpen(false)} className="btn-primary justify-center" data-testid="mobile-dashboard-btn">
                  Mon espace
                </Link>
                <button onClick={handleLogout} className="btn-outline justify-center mt-2" data-testid="mobile-logout-btn">
                  Déconnexion
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-outline justify-center" data-testid="mobile-login-btn">
                  Se connecter
                </Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-primary justify-center mt-2" data-testid="mobile-register-btn">
                  Créer un compte
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
